import { useCallback, useEffect, useMemo, useState } from 'react';

import { getPolicies, type PolicySummary } from '../api';
import {
  approvePublishPlan,
  getPublishExecutionHistory,
  getPublishPlan,
  inspectPublishRecovery,
  revokePublishApproval,
  type PublishExecutionSummary,
  type PublishPlanResponse,
  type PublishRecoveryResponse,
} from './publishApi';

function eventValue(event: Event): string {
  return (event.currentTarget as HTMLElement & { value: string }).value;
}

function readinessLabel(value: PublishPlanResponse): string {
  if (!value.gate.passed) return 'BLOCKED — IMPACT GATE REQUIRED';
  if (!value.gate.current) return 'BLOCKED — IMPACT EVIDENCE IS STALE';
  if (!value.plan.supported) return 'BLOCKED — NO LOSSLESS SHOPIFY MAPPING';
  if (value.approval.approved) return 'APPROVED — EXECUTION STILL DISABLED';
  if (value.approval.prior_approval_exists) return 'REVIEW AGAIN — PRIOR APPROVAL IS NOT CURRENT';
  return 'PUBLISH PLAN READY FOR REVIEW';
}

function executionLabel(status: string): string {
  return status.replaceAll('_', ' ').toUpperCase();
}

function stateValue(value: { type: string | null; value: string | null } | null): string {
  if (!value) return 'unavailable';
  const kind = value.type ?? 'none';
  return value.value === null ? kind : `${kind} ${value.value}`;
}

export function PublishReadiness() {
  const [policies, setPolicies] = useState<PolicySummary[]>([]);
  const [policyId, setPolicyId] = useState('');
  const [result, setResult] = useState<PublishPlanResponse | null>(null);
  const [history, setHistory] = useState<PublishExecutionSummary[]>([]);
  const [recovery, setRecovery] = useState<PublishRecoveryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [recoveryBusyId, setRecoveryBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedPolicy = useMemo(
    () => policies.find((policy) => policy.id === policyId) ?? null,
    [policies, policyId],
  );

  const refresh = useCallback(async (selectedId: string) => {
    if (!selectedId) {
      setResult(null);
      setHistory([]);
      setRecovery(null);
      return;
    }

    setHistoryLoading(true);
    try {
      const [plan, executions] = await Promise.all([
        getPublishPlan(selectedId),
        getPublishExecutionHistory(selectedId),
      ]);
      setResult(plan);
      setHistory(executions.items);
      setRecovery(null);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPolicies()
      .then(async (rows) => {
        if (!active) return;
        const drafts = rows.filter((policy) => policy.draft_version !== null);
        setPolicies(drafts);
        const first = drafts[0]?.id ?? '';
        setPolicyId(first);
        if (first) {
          const [plan, executions] = await Promise.all([
            getPublishPlan(first),
            getPublishExecutionHistory(first),
          ]);
          if (active) {
            setResult(plan);
            setHistory(executions.items);
          }
        }
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(reason instanceof Error ? reason.message : 'Could not load publish readiness.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const selectPolicy = useCallback(
    async (event: Event) => {
      const value = eventValue(event);
      setPolicyId(value);
      setResult(null);
      setHistory([]);
      setRecovery(null);
      setError(null);
      try {
        await refresh(value);
      } catch (reason: unknown) {
        setError(reason instanceof Error ? reason.message : 'Could not compile publish plan.');
      }
    },
    [refresh],
  );

  const approve = useCallback(async () => {
    if (!policyId || !result?.plan.supported || result.approval.approved) return;
    setApprovalBusy(true);
    setError(null);
    try {
      setResult(await approvePublishPlan(policyId, result.plan.fingerprint));
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not approve publish plan.');
      try {
        await refresh(policyId);
      } catch {
        // Keep the original approval error visible.
      }
    } finally {
      setApprovalBusy(false);
    }
  }, [policyId, refresh, result]);

  const revoke = useCallback(async () => {
    if (!policyId || !result?.approval.approved) return;
    setApprovalBusy(true);
    setError(null);
    try {
      await revokePublishApproval(policyId);
      await refresh(policyId);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not revoke publish approval.');
    } finally {
      setApprovalBusy(false);
    }
  }, [policyId, refresh, result?.approval.approved]);

  const inspectRecovery = useCallback(async (executionId: string) => {
    setRecoveryBusyId(executionId);
    setRecovery(null);
    setError(null);
    try {
      setRecovery(await inspectPublishRecovery(executionId));
    } catch (reason: unknown) {
      setError(
        reason instanceof Error ? reason.message : 'Could not inspect publish recovery state.',
      );
    } finally {
      setRecoveryBusyId(null);
    }
  }, []);

  const canApprove = Boolean(
    result &&
      result.gate.passed &&
      result.gate.current &&
      result.plan.supported &&
      !result.approval.approved,
  );

  return (
    <s-section heading="Publish readiness">
      <s-stack direction="block" gap="base">
        <s-paragraph>
          Atellius compiles a passed draft into Shopify-native mutations for review. Merchant
          approval is recorded against the exact reviewed fingerprint, but Shopify execution remains
          disabled until remote validation is complete.
        </s-paragraph>

        {loading && <s-paragraph>Loading publish planning…</s-paragraph>}
        {!loading && policies.length === 0 && (
          <s-paragraph>Create a draft policy before checking publish readiness.</s-paragraph>
        )}

        {policies.length > 0 && (
          <s-select label="Draft to compile" value={policyId} onChange={selectPolicy}>
            {policies.map((policy) => (
              <s-option key={policy.id} value={policy.id}>
                {policy.name} · draft v{policy.draft_version}
              </s-option>
            ))}
          </s-select>
        )}

        {selectedPolicy && result && (
          <s-stack direction="block" gap="small">
            <s-heading>{readinessLabel(result)}</s-heading>
            <s-paragraph>
              Impact gate: {result.gate.passed ? 'passed' : 'not passed'} · evidence:{' '}
              {result.gate.current ? 'current' : 'stale or unavailable'}
            </s-paragraph>

            {result.plan.catalog_id && (
              <s-paragraph>
                Target catalog: {result.plan.catalog_id}
                {result.plan.price_list_id ? ` · price list ${result.plan.price_list_id}` : ''}
              </s-paragraph>
            )}

            {result.plan.blockers.map((blocker) => (
              <s-paragraph key={blocker}>Blocker: {blocker}</s-paragraph>
            ))}

            {result.plan.warnings.map((warning) => (
              <s-paragraph key={warning}>Warning: {warning}</s-paragraph>
            ))}

            {result.plan.mutations.map((mutation, index) => (
              <s-stack key={`${mutation.kind}-${index}`} direction="block" gap="small">
                <s-heading>{mutation.kind}</s-heading>
                <s-paragraph>{mutation.description}</s-paragraph>
              </s-stack>
            ))}

            {result.plan.supported && (
              <>
                <s-paragraph>
                  Required Shopify scopes: {result.plan.required_scopes.join(', ') || 'none'}
                </s-paragraph>
                <s-paragraph>Plan fingerprint: {result.plan.fingerprint}</s-paragraph>
              </>
            )}

            {result.approval.approved && (
              <s-stack direction="block" gap="small">
                <s-heading>Merchant approval recorded</s-heading>
                <s-paragraph>
                  Approved {result.approval.approved_at ?? ''}
                  {result.approval.approved_by_current_user
                    ? ' by this Shopify user.'
                    : ' by another Shopify user.'}
                </s-paragraph>
                <s-button onClick={() => void revoke()} disabled={approvalBusy}>
                  {approvalBusy ? 'Updating approval…' : 'Revoke approval'}
                </s-button>
              </s-stack>
            )}

            {canApprove && (
              <s-stack direction="block" gap="small">
                <s-paragraph>
                  Approving confirms that you reviewed this exact plan fingerprint. If the draft,
                  Shopify sync, impact evidence, or compiled plan changes, this approval will no
                  longer be current.
                </s-paragraph>
                <s-button onClick={() => void approve()} disabled={approvalBusy}>
                  {approvalBusy ? 'Recording approval…' : 'Approve exact publish plan'}
                </s-button>
              </s-stack>
            )}

            <s-button disabled>Shopify execution disabled</s-button>

            <s-heading>Execution history</s-heading>
            {historyLoading && <s-paragraph>Loading publish execution history…</s-paragraph>}
            {!historyLoading && history.length === 0 && (
              <s-paragraph>
                No Shopify publish execution has been created for this policy. Approval alone does
                not change Shopify.
              </s-paragraph>
            )}

            {history.map((execution) => (
              <s-stack key={execution.id} direction="block" gap="small">
                <s-heading>{executionLabel(execution.status)}</s-heading>
                <s-paragraph>
                  {execution.applied_mutations}/{execution.mutation_count} planned mutation(s)
                  recorded as applied · created {execution.created_at}
                </s-paragraph>
                {execution.error && <s-paragraph>Recorded error: {execution.error}</s-paragraph>}
                {execution.recovery_review_recommended && (
                  <s-button
                    onClick={() => void inspectRecovery(execution.id)}
                    disabled={recoveryBusyId !== null}
                  >
                    {recoveryBusyId === execution.id
                      ? 'Inspecting live Shopify state…'
                      : 'Inspect recovery — read only'}
                  </s-button>
                )}
              </s-stack>
            ))}

            {recovery && (
              <s-stack direction="block" gap="small">
                <s-heading>Recovery inspection — read only</s-heading>
                <s-paragraph>
                  Execution {executionLabel(recovery.execution.status)} · Shopify classification:{' '}
                  {executionLabel(recovery.shopify.classification)}
                </s-paragraph>
                <s-paragraph>
                  Price list {recovery.shopify.price_list_id} · baseline{' '}
                  {stateValue(recovery.shopify.baseline)} · desired{' '}
                  {stateValue(recovery.shopify.desired)} · observed{' '}
                  {stateValue(recovery.shopify.observed)}
                </s-paragraph>
                <s-paragraph>
                  Approved Atellius version v{recovery.atellius.approved_version_number} is{' '}
                  {recovery.atellius.approved_version_state}.
                </s-paragraph>
                <s-paragraph>Recommended handling: {recovery.recommendation}</s-paragraph>
                <s-paragraph>
                  No retry, rollback, or recovery mutation is available from this inspection.
                </s-paragraph>
              </s-stack>
            )}
          </s-stack>
        )}

        {error && <s-paragraph>Publish readiness needs attention: {error}</s-paragraph>}
      </s-stack>
    </s-section>
  );
}
